import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaImage, FaCalendarAlt, FaNewspaper, FaTimes, FaBuilding, FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api, { BASE_URL } from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import { useNotifications } from '../../../../context/NotificationContext';
import useColleges from '../../../../utils/useColleges';
import './AddNews.css';

const API_BASE = BASE_URL;

const EMPTY_FORM = {
  title: '',
  content: '',
  date: new Date().toISOString().split('T')[0],
  colleges: []
};

const AddNews = () => {
  const { user } = useAuth();
  const { refresh: refreshNotifications } = useNotifications();
  const { colleges: assignedColleges, loading: collegesLoading } = useColleges();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collegeSearch, setCollegeSearch] = useState('');
  const [showCollegeDrop, setShowCollegeDrop] = useState(false);
  const collegeDropRef = useRef(null);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editId, setEditId] = useState(null);
  const [expandedNewsId, setExpandedNewsId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      if (collegeDropRef.current && !collegeDropRef.current.contains(e.target))
        setShowCollegeDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNews = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/news', { params: { limit: 100, manage: 'true' } });
      setNewsList(data.data || []);
    } catch (error) {
      console.error('Failed to load news:', error);
      Swal.fire('Error', 'Failed to load news articles.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleCollege = (id) => {
    setForm(prev => {
      const exists = prev.colleges.includes(id);
      return {
        ...prev,
        colleges: exists ? prev.colleges.filter(c => c !== id) : [...prev.colleges, id]
      };
    });
  };

  const toggleSelectAll = () => {
    setForm(prev => {
      const all = assignedColleges.map(c => String(c._id));
      const isAllSelected = all.length > 0 && all.every(id => prev.colleges.includes(id));
      return { ...prev, colleges: isAllSelected ? [] : all };
    });
  };

  const allSelected = assignedColleges.length > 0 && assignedColleges.every(c => form.colleges.includes(String(c._id)));
  const filteredCollegeOptions = assignedColleges.filter(c => !collegeSearch || c.name.toLowerCase().includes(collegeSearch.toLowerCase()));
  const selectedCollegeNames = form.colleges.length === 0 ? '-- Select College(s) --' : allSelected ? 'All Colleges' : assignedColleges.filter(c => form.colleges.includes(String(c._id))).map(c => c.name).join(', ');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('File Too Large', 'Please select an image smaller than 5MB.', 'warning');
      return;
    }
    setImageFile(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const validateForm = () => {
    if (!form.title.trim()) { Swal.fire('Missing Title', 'Please enter a news title.', 'warning'); return false; }
    if (form.colleges.length === 0) { Swal.fire('Missing Colleges', 'Please select at least one college.', 'warning'); return false; }
    if (!form.content.trim()) { Swal.fire('Missing Content', 'Please enter news content.', 'warning'); return false; }
    return true;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setPreviewImage(null);
    setEditId(null);
    setCollegeSearch('');
    setShowCollegeDrop(false);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('content', form.content.trim());
      formData.append('date', form.date);
      formData.append('colleges', JSON.stringify(form.colleges));
      if (imageFile) formData.append('image', imageFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editId) {
        const { data } = await api.put(`/news/${editId}`, formData, config);
        setNewsList(prev => prev.map(news => news._id === editId ? data.data : news));
        Swal.fire('Updated!', 'News article has been updated successfully.', 'success');
      } else {
        const { data } = await api.post('/news', formData, config);
        setNewsList(prev => [data.data, ...prev]);
        Swal.fire('📰 Published!', 'Your news article is now live.', 'success');
        if (refreshNotifications) setTimeout(() => refreshNotifications(), 1000);
      }
      resetForm();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to save news article.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (news) => {
    setEditId(news._id);
    setForm({
      title: news.title || '',
      content: news.content || '',
      date: news.date ? news.date.split('T')[0] : new Date().toISOString().split('T')[0],
      colleges: (news.colleges || []).map(c => String(c._id || c))
    });
    setImageFile(null);
    setPreviewImage(news.image ? `${API_BASE}/${news.image}` : null);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleStatus = async (news) => {
    try {
      const { data } = await api.patch(`/news/${news._id}/toggle`);
      setNewsList(prev => prev.map(n => n._id === news._id ? { ...n, isActive: data.data.isActive } : n));
      if (refreshNotifications) setTimeout(() => refreshNotifications(), 500);
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  const handleDelete = (news) => {
    Swal.fire({
      title: `Delete "${news.title}"?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await api.delete(`/news/${news._id}`);
        setNewsList(prev => prev.filter(n => n._id !== news._id));
        Swal.fire('Deleted!', 'News article has been deleted.', 'success');
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Failed to delete news article.', 'error');
      }
    });
  };

  const getImageUrl = (news) => {
    if (!news.image) return null;
    if (news.image.startsWith('http')) return news.image;
    let imagePath = news.image.replace(/\\/g, '/');
    return imagePath.startsWith('uploads/') ? `${API_BASE}/${imagePath}` : `${API_BASE}/uploads/${imagePath}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="add-news-container">
      <h2 className="add-news-title">News Management</h2>

      <div className="add-news-controls">
        <button className="add-news-btn add-news-btn-add" onClick={() => { showAddForm ? resetForm() : setShowAddForm(true); }}>
          {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add News</>}
        </button>
      </div>

      {showAddForm && (
        <div className="add-news-inline-form">
          <h3 className="add-news-form-title">{editId ? '✏️ Edit News Article' : '📰 Add New News Article'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="add-news-form-group">
              <label>News Title <span className="add-news-req">*</span></label>
              <input type="text" name="title" value={form.title} onChange={handleInputChange} placeholder="Enter compelling news headline..." maxLength="200" />
            </div>

            {assignedColleges.length > 0 && (
              <div className="add-news-form-group">
                <label><FaBuilding style={{ marginRight: '6px' }} />Select Colleges <span className="add-news-req">*</span></label>
                <div className="cls-college-dropdown-wrapper" ref={collegeDropRef}>
                  <div className="cls-college-select-box" onClick={() => setShowCollegeDrop(v => !v)}>
                    <span style={{ color: form.colleges.length ? '#1e293b' : '#94a3b8', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedCollegeNames}
                    </span>
                    <FaChevronDown style={{ fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }} />
                  </div>
                  {showCollegeDrop && (
                    <div className="cls-college-dropdown">
                      <div className="cls-college-search">
                        <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem' }} />
                        <input type="text" placeholder="Search college..." value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)} autoFocus />
                      </div>
                      {!collegeSearch && (
                        <div className="cls-college-option cls-select-all" onClick={toggleSelectAll}>
                          <input type="checkbox" checked={allSelected} readOnly />
                          <span>Select All</span>
                        </div>
                      )}
                      <div className="cls-college-options">
                        {filteredCollegeOptions.map(c => (
                          <div key={c._id} className="cls-college-option" onClick={() => toggleCollege(String(c._id))}>
                            <input type="checkbox" checked={form.colleges.includes(String(c._id))} readOnly />
                            <span>{c.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="add-news-image-upload-wrapper">
              <div className="add-news-image-preview">
                {previewImage ? <img src={previewImage} alt="Preview" /> : (
                  <div className="add-news-placeholder-content">
                    <FaImage className="add-news-placeholder-icon" />
                    <span>16:9 Aspect Ratio</span>
                  </div>
                )}
              </div>
              <div className="add-news-upload-controls">
                <label className="add-news-upload-btn">
                  {previewImage ? 'Change Image' : 'Upload News Image'}
                  <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                </label>
                <span className="add-news-upload-hint">Recommended: 800x450px (16:9 Ratio, JPG/PNG, max 5MB)</span>
              </div>
            </div>

            <div className="add-news-form-group">
              <label><FaCalendarAlt style={{ marginRight: '6px' }} />News Date <span className="add-news-req">*</span></label>
              <input type="date" name="date" value={form.date} onChange={handleInputChange} />
            </div>

            <div className="add-news-form-group">
              <label>News Content <span className="add-news-req">*</span></label>
              <textarea name="content" value={form.content} onChange={handleInputChange} rows="8" placeholder="Write the complete news content here..." maxLength="5000" />
            </div>

            <div className="add-news-form-buttons">
              <button type="button" className="add-news-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
              <button type="submit" className="add-news-btn-confirm" disabled={saving}>
                {saving ? 'Saving...' : editId ? 'Update News' : 'Publish News'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Responsive News List Table ── */}
      <div className="add-news-list-section">
        <h3 className="add-news-section-title">
          <FaNewspaper style={{ marginRight: '8px' }} /> Published News Articles ({newsList.length})
        </h3>

        {loading ? (
          <div className="add-news-loading">Loading news articles...</div>
        ) : newsList.length === 0 ? (
          <div className="add-news-empty">No news articles found. Create your first news article!</div>
        ) : (
          <div className="add-news-table-wrapper">
            <table className="add-news-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th className="add-news-table-image-col">Image</th>
                  <th className="add-news-align-left">Title</th>
                  <th className="add-news-table-college-col">Colleges</th>
                  <th className="add-news-table-date-col">Date</th>
                  <th className="add-news-table-status-col">Status</th>
                  <th className="add-news-table-action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {newsList.map((news, index) => (
                  <React.Fragment key={news._id}>
                    <tr className="add-news-main-row">
                      <td>{index + 1}</td>
                      <td className="add-news-table-image-col">
                        <div className="add-news-table-image">
                          {getImageUrl(news) ? (
                            <img src={getImageUrl(news)} alt={news.title} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          ) : null}
                          <div className="add-news-image-placeholder" style={{ display: getImageUrl(news) ? 'none' : 'flex' }}><FaImage /></div>
                        </div>
                      </td>
                      <td className="add-news-align-left">
                        <div className="add-news-title-wrapper" onClick={() => setExpandedNewsId(expandedNewsId === news._id ? null : news._id)}>
                          <span className="add-news-title-text">{news.title}</span>
                          <button className="add-news-expand-btn">
                            {expandedNewsId === news._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>
                      <td className="add-news-table-college-col">
                        <span className="add-news-college-badge">
                          {news.colleges?.length > 0 ? `${news.colleges.length} College${news.colleges.length > 1 ? 's' : ''}` : 'All Colleges'}
                        </span>
                      </td>
                      <td className="add-news-table-date-col">
                        <div className="add-news-date-badge"><FaCalendarAlt style={{ marginRight: '5px' }} />{formatDate(news.date)}</div>
                      </td>
                      <td className="add-news-table-status-col">
                        <label className="add-news-switch">
                          <input type="checkbox" checked={news.isActive !== false} onChange={() => handleToggleStatus(news)} />
                          <span className="add-news-slider" />
                        </label>
                        <div style={{ fontSize: '0.7rem', marginTop: '4px', color: news.isActive !== false ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                          {news.isActive !== false ? 'Active' : 'Disabled'}
                        </div>
                      </td>
                      <td className="add-news-table-action-col">
                        <div className="add-news-action-btns">
                          <button className="add-news-btn-edit" onClick={() => handleEdit(news)} title="Edit"><FaEdit /></button>
                          <button className="add-news-btn-delete" onClick={() => handleDelete(news)} title="Delete"><FaTrash /></button>
                        </div>
                      </td>
                    </tr>

                    {/* ── Expanded Row Content ── */}
                    {expandedNewsId === news._id && (
                      <tr className="add-news-expanded-row">
                        <td colSpan="7">
                          <div className="add-news-expanded-content">

                            <div className="add-news-exp-item add-news-exp-date">
                              <span className="add-news-exp-label">Date Published</span>
                              <span className="add-news-exp-value">{formatDate(news.date)}</span>
                            </div>

                            <div className="add-news-exp-item add-news-exp-status">
                              <span className="add-news-exp-label">Status</span>
                              <div className="add-news-exp-value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label className="add-news-switch">
                                  <input type="checkbox" checked={news.isActive !== false} onChange={() => handleToggleStatus(news)} />
                                  <span className="add-news-slider" />
                                </label>
                                <span style={{ color: news.isActive !== false ? '#27ae60' : '#e74c3c', fontWeight: 700, fontSize: '0.85rem' }}>
                                  {news.isActive !== false ? 'Active' : 'Disabled'}
                                </span>
                              </div>
                            </div>

                            <div className="add-news-exp-item add-news-exp-college-names" style={{ gridColumn: '1 / -1' }}>
                              <span className="add-news-exp-label">Assigned Colleges</span>
                              <div className="add-news-exp-college-badges">
                                {news.colleges?.length > 0
                                  ? news.colleges.map(c => (
                                      <span key={c._id || c} className="add-news-college-badge">{c.name || c}</span>
                                    ))
                                  : <span className="add-news-college-badge all">All Colleges</span>
                                }
                              </div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddNews;